import "./login.page.css";

import { useRef, useState, useEffect, useReducer } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

// Const
import Nuclias from "../../const/nuclias";

// Component
import Input from '../../components/Input';
import InputWithIcon from '../../components/InputWithIcon';
import Checkbox from '../../components/Checkbox';
import Button from '../../components/Button';
import DropdownWithItem from '../../components/DropdownWithItem';
import LinkerWithRoute from '../../components/LinkerWithRoute';
import MessageBoxGroup from "../../components/MessageBoxGroup";

// UI
import Footer from "../../ui/Footer";

// Service
import oauthService from '../../services/OAuth.service';

// Model
import User from "../../models/User";

// Slice
import { uiActions } from "../../stores/slice/ui";
import { userActions } from "../../stores/slice/user";

// Hook
import useLang from "../../hooks/useLang";

// Local Store
import loginStore from "./store";
import StoreManager from "./StoreManager";

// Default value
const defaultMessages = {
  success: null,
  error: null,
  warning: null,
};

const Login = () => {
  const ref = useRef();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Check pre load did finish
  const publicKey = useSelector(state => state.publicKey);
  const serverSetting = useSelector(state => state.serverSetting);

  const [messages, setMessages] = useState({ ...defaultMessages });

  useEffect(() => {
    const token = User.getTokenAndAliveTime();
    if (publicKey && serverSetting && User.getTokenAndAliveTime() && token) {
      navigate('/');
    }
  }, [dispatch, publicKey, serverSetting, navigate]);


  // Handle lang
  const { langList, selectedLang, changeLang } = useLang();

  // Handle log In
  const [loginState, loginDispatch] = useReducer(
    loginStore.reducer,
    loginStore.defaultState
  );

  StoreManager.state = loginState;
  StoreManager.action = loginStore.action;
  StoreManager.dispatch = loginDispatch;

  const checkForm = () => {
    if (
      (StoreManager.state.email.isDirty && StoreManager.state.email.isValid) &&
      (StoreManager.state.password.isDirty && StoreManager.state.password.isValid)
    ) {
      return true;
    }
    return false;
  };

  const logIn = async () => {
    dispatch(uiActions.showSpinner());
    const encrypedPassword = User.encryptPassword(publicKey.value.publicKey, loginState.password.value);

    try {
      const response = await oauthService.getToken(StoreManager.state.email.value, encrypedPassword);

      if (response.error) {
        throw response.error;
      }

      User.saveTokenKeepAliveToLocalStoreage(loginState.stayLoggedIn);
      User.saveTokenToLocalStoreage(response);

      dispatch(userActions.setToken({
        token: localStorage.getItem(Nuclias.localStorageKey.token),
        tokenAliveTime: localStorage.getItem(Nuclias.localStorageKey.tokenAliveTime)
      }));

      oauthService.cleanDataAndError();
      navigate('/');
    } catch (error) {
      setMessages({
        ...messages,
        error: error.code,
        success: null,
      });
      dispatch(uiActions.hideSpinner());
    }
  };

  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.keyCode === 13 && checkForm()) {
        ref.current.querySelector('.btn-login').click();
      }
    };

    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  const renderPage = () => {
    return (
      <div className="body" ref={ref}>
        <MessageBoxGroup
          containerMode={true}
          messages={messages}
          ErrorMessagesType='es'
          SuccessMessagesType='i18n'
          WarningMessagesType='i18n'
          onClose={(value) => setMessages({ ...defaultMessages, [value]: null })}
        />
        <div className="body-content">
          <div className="lang">
            <DropdownWithItem
              id="lang-dropdown"
              type="icon"
              extendClassName="icon-language"
              selectedItem={selectedLang}
              itemList={langList}
              onClick={(item) => changeLang(item)}
            />
          </div>

          <div className="small-block-margin"></div>
          <div className="logo"></div>
          <div className="big-block-margin"></div>

          <Input
            type="text"
            placeholder="Email"
            isInvalid={StoreManager.checkFieldIsValid('email')}
            value={StoreManager.state.email.value}
            onClick={() => StoreManager.toggleEmailHint(true)}
            onBlur={() => StoreManager.toggleEmailHint(false)}
            onChange={(e) => StoreManager.changeEmail(e.target.value)}
          />

          {StoreManager.state.showEmailHint && <div className="message-container">1-128 characters</div>}
          {!StoreManager.state.showEmailHint && <div className="small-block-margin"></div>}

          {StoreManager.state.password.isText && (
            <InputWithIcon
              type="password"
              placeholder="Password"
              value={StoreManager.state.password.value}
              isInvalid={StoreManager.checkFieldIsValid('password')}
              onClick={() => StoreManager.togglePasswordHint(true)}
              onBlur={() => StoreManager.togglePasswordHint(false)}
              onChange={(e) => StoreManager.changePassword(e.target.value)}
              iconTitle="Show password"
              iconClassName="icon-open-eye"
              iconOnClick={() => StoreManager.toggleEye()}
            />)
          }

          {!StoreManager.state.password.isText && (
            <InputWithIcon
              type="text"
              placeholder="Password"
              value={StoreManager.state.password.value}
              isInvalid={StoreManager.checkFieldIsValid('password')}
              onChange={(e) => StoreManager.changePassword(e.target.value)}
              iconTitle="Hide password"
              iconClassName="icon-close-eye"
              iconOnClick={() => StoreManager.toggleEye()}
            />)
          }

          {StoreManager.state.showPasswordHint && <div className="message-container">8-64 characters</div>}
          {!StoreManager.state.showPasswordHint && <div className="small-block-margin"></div>}

          <Checkbox
            id="ignoreIdleTimeout"
            htmlFor="ignoreIdleTimeout"
            type="checkbox"
            style={{ marginBottom: '-8px' }}
            label={t("77dcc41234")}
            checked={loginState.stayLoggedIn}
            onChange={() => StoreManager.clickStayLogginedIn()}
          />
          <div className="small-block-margin"></div>

          <Button
            label={t("bffe9a3c9a")}
            className="btn-login"
            disabled={!checkForm()}
            onClick={logIn}
          />

          <div className="combo-between">
            <LinkerWithRoute label={t("9ec54ec336")} to="/forgot_password" className="linker-blue text-decoration-none mt-1" onClick={() => { }}></LinkerWithRoute>
            <LinkerWithRoute label={t("739405e73c")} to="/create_account" className="linker-blue text-decoration-none mt-1" onClick={() => { }}></LinkerWithRoute>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div id="login">
        {renderPage()}
        <Footer />
      </div>
    </>
  );
};

export default Login;